/**
 * Transaction Detail Modal
 * TASK-213: Build Transaction Detail Modal
 *
 * Comprehensive modal for reviewing flagged transactions with AI risk analysis,
 * FINMA regulation references, and compliance workflow actions.
 *
 * @component
 * @example
 * <TransactionDetailModal
 *   isOpen={true}
 *   transactionAlert={mockAlert}
 *   onClose={() => {}}
 *   onTriggerWorkflow={(type) => console.log(type)}
 * />
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { TransactionAlert, WorkflowType } from '../../types/compliance';
import TransactionHeader from './TransactionHeader';
import TransactionInfoCard from './TransactionInfoCard';
import AIAnalysisPanel from './AIAnalysisPanel';
import FINMAReferencesCard from './FINMAReferencesCard';
import CommentSection from './CommentSection';
import ActionButtons from './ActionButtons';
import RelatedTransactionsSidebar from './RelatedTransactionsSidebar';

interface TransactionDetailModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Transaction alert data */
  transactionAlert: TransactionAlert;
  /** Close handler */
  onClose: () => void;
  /** Workflow trigger handler */
  onTriggerWorkflow: (workflowType: WorkflowType) => void;
  /** Document upload handler */
  onUploadDocument: () => void;
  /** Comment submit handler */
  onAddComment: (comment: string) => void;
  /** Dismiss alert handler */
  onDismiss: () => void;
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  isOpen,
  transactionAlert,
  onClose,
  onTriggerWorkflow,
  onUploadDocument,
  onAddComment,
  onDismiss,
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, onClose]);

  // Early return if not open
  if (!isOpen) return null;

  // Check if transaction is blocked (can't be dismissed)
  const isBlocked = transactionAlert.aiAnalysis.flags.some(
    (flag) => flag.type === 'sanctions_risk'
  );

  // Convert RiskLevel to AlertLevel (map 'low' to 'info')
  const alertLevel = (transactionAlert.aiAnalysis.riskLevel === 'low'
    ? 'info'
    : transactionAlert.aiAnalysis.riskLevel) as 'critical' | 'high' | 'medium' | 'info';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="transaction-modal-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <TransactionHeader
            transactionId={transactionAlert.transactionId}
            timestamp={transactionAlert.timestamp}
            riskScore={transactionAlert.aiAnalysis.riskScore}
            riskLevel={alertLevel}
          />
          <button
            onClick={onClose}
            className="ml-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 p-6">
            {/* Main Content */}
            <div className="space-y-6">
              {/* Transaction Info */}
              <TransactionInfoCard transactionAlert={transactionAlert} />

              {/* AI Risk Analysis */}
              <AIAnalysisPanel
                riskScore={transactionAlert.aiAnalysis.riskScore}
                riskLevel={transactionAlert.aiAnalysis.riskLevel}
                flags={transactionAlert.aiAnalysis.flags}
                recommendation={transactionAlert.aiAnalysis.recommendation}
                documentsRequired={transactionAlert.aiAnalysis.documentsRequired}
                additionalChecks={transactionAlert.aiAnalysis.additionalChecks}
                contextualNote={transactionAlert.aiAnalysis.contextualNote}
              />

              {/* FINMA Regulations */}
              {transactionAlert.aiAnalysis.finmaReferences.length > 0 && (
                <FINMAReferencesCard
                  references={transactionAlert.aiAnalysis.finmaReferences}
                  expandable={true}
                />
              )}

              {/* Comments Section */}
              <CommentSection onAddComment={onAddComment} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <RelatedTransactionsSidebar
                clientId={transactionAlert.relatedClientId}
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <ActionButtons
            onDismiss={isBlocked ? undefined : onDismiss}
            onUploadDocument={onUploadDocument}
            onTriggerWorkflow={onTriggerWorkflow}
            isBlocked={isBlocked}
            aiRecommendation={transactionAlert.aiAnalysis.recommendation}
          />
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;
